import React, { useState } from 'react';
import { Section } from '../../stores/builderStore';
import { useBuilderStore } from '../../stores/builderStore';
import { StyleEditor } from './StyleEditor';
import { ContentEditor } from './ContentEditor';
import { ResponsiveEditor } from './ResponsiveEditor';
import { AnimationEditor } from './AnimationEditor';
import { X, Palette, Type, Smartphone, Sparkles, Settings } from 'lucide-react';

interface PropertyEditorProps {
  section: Section;
  onClose: () => void;
}

type TabType = 'content' | 'style' | 'responsive' | 'animation' | 'advanced';

export const PropertyEditor: React.FC<PropertyEditorProps> = ({ section: propSection, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('content');
  const updateSection = useBuilderStore(state => state.updateSection);

  // Get the current section from store to ensure we have fresh data
  let section = useBuilderStore(state =>
    state.sections.find(s => s.id === propSection.id)
  ) || propSection;

  // Ensure type field exists (fallback to section_type)
  if (!section.type && (section as any).section_type) {
    section = {
      ...section,
      type: (section as any).section_type
    };
  }

  // Debug logging
  console.log('PropertyEditor using section:', {
    id: section.id,
    type: section.type,
    section_type: (section as any).section_type,
    title: section.title,
    hasType: !!section.type,
    typeValue: section.type || 'UNDEFINED',
    props: section.props
  });

  const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
    { id: 'content', label: 'Content', icon: Type },
    { id: 'style', label: 'Style', icon: Palette },
    { id: 'responsive', label: 'Responsive', icon: Smartphone },
    { id: 'animation', label: 'Animation', icon: Sparkles },
    { id: 'advanced', label: 'Advanced', icon: Settings },
  ];

  const handleUpdateProps = (newProps: any) => {
    console.log('PropertyEditor - Updating props with:', newProps);
    console.log('Current props:', section.props);
    const updatedProps = { ...section.props, ...newProps };
    console.log('Merged props:', updatedProps);
    updateSection(section.id, { props: updatedProps });
  };

  const handleUpdateStyles = (newStyles: any) => {
    updateSection(section.id, { styles: { ...section.styles, ...newStyles } });
  };

  const handleUpdateResponsive = (newResponsive: any) => {
    updateSection(section.id, { responsive: { ...section.responsive, ...newResponsive } });
  };

  const handleUpdateAnimations = (newAnimations: any) => {
    updateSection(section.id, { animations: { ...section.animations, ...newAnimations } });
  };

  return (
    <div className="w-80 bg-white border-l h-screen flex flex-col shrink-0">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">{section.title}</h3>
          <p className="text-sm text-gray-500">Type: {section.type}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-2 py-3 text-sm font-medium transition-colors flex flex-col items-center gap-1 ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-xs">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="p-4 overflow-y-scroll" style={{ height: 'calc(100vh - 200px)' }}>
        {activeTab === 'content' && (
          <ContentEditor
            section={section}
            onChange={handleUpdateProps}
          />
        )}

        {activeTab === 'style' && (
          <StyleEditor
            styles={section.styles}
            onChange={handleUpdateStyles}
          />
        )}

        {activeTab === 'responsive' && (
          <ResponsiveEditor
            responsive={section.responsive}
            onChange={handleUpdateResponsive}
          />
        )}

        {activeTab === 'animation' && (
          <AnimationEditor
            animations={section.animations || {}}
            onChange={handleUpdateAnimations}
          />
        )}

        {activeTab === 'advanced' && (
          <div className="space-y-4">
            {/* Section ID */}
            <div>
              <label className="block text-sm font-medium mb-1">Section ID</label>
              <input
                type="text"
                value={section.id}
                disabled
                className="w-full px-3 py-2 border rounded-lg bg-gray-50"
              />
            </div>

            {/* Custom CSS Class */}
            <div>
              <label className="block text-sm font-medium mb-1">CSS Classes</label>
              <input
                type="text"
                placeholder="custom-class another-class"
                value={section.props.className || ''}
                onChange={(e) => handleUpdateProps({ className: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            {/* HTML Attributes */}
            <div>
              <label className="block text-sm font-medium mb-1">HTML ID</label>
              <input
                type="text"
                placeholder="section-id"
                value={section.props.htmlId || ''}
                onChange={(e) => handleUpdateProps({ htmlId: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            {/* Z-Index */}
            <div>
              <label className="block text-sm font-medium mb-1">Z-Index</label>
              <input
                type="number"
                value={section.styles?.zIndex || 0}
                onChange={(e) => handleUpdateStyles({ zIndex: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};